import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';

interface AvatarProps {
  src?: string | null;
  alt: string;
  className?: string;
  fallbackClassName?: string;
}

export const Avatar: FC<AvatarProps> = ({ src, alt, className, fallbackClassName }) => {
  const [hasError, setHasError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsImageLoaded(true);
    }
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  const handleLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  if (!src || hasError) {
    return <div className={cn('rounded-full bg-gray-200 shrink-0', fallbackClassName, className)} />;
  }

  return (
    <div className={cn('relative overflow-hidden rounded-full shrink-0', className)}>
      {!isImageLoaded && <div className="absolute inset-0 animate-pulse bg-black/10" />}
      <img
        ref={imgRef}
        src={communityImageSrc(src, 256)}
        alt={alt}
        className={cn('size-full object-cover transition-opacity duration-200', {
          'opacity-0': !isImageLoaded,
          'opacity-100': isImageLoaded,
        })}
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
};
